import classNames from "classnames";
import { first } from "lodash";
import { filterPathIntersectionCached } from "oc-app/metadata/GraphMetadata";
import { maxFrequent } from "oc-commons/Commons";
import { useState } from "react";
import { I18N } from "oc-I18N";
import { MAuswahlwert, MInspiration } from "../../metamodell";
import { PInspiration } from "../../metamodell_paths";
import { gotoPage } from "./PageButtons";

export interface InspirationGroupProps {
  inspirationen: MInspiration[];
  detailSerie: MAuswahlwert;
}

/**
 * Eine Inspirationsgruppe ist definiert über DetailSerie, Zaunart und Inspirations-Auswahlwerte
 * Der Inspirations-Auswahlwert legt das angezeigte bild UND den selektierten Farbkreis fest (Vorauswahl).
 * Die Farbkategorie kann sortiert werden, hat aber keine Vorauswahl
 *
 * @export
 * @class InspirationGroup
 */
export function InspirationGroup({ inspirationen, detailSerie }: InspirationGroupProps) {
  const agt = () => APP.arrangement;
  const auswahlwertVoreinstellung: MAuswahlwert = APP.richMetadata.getAuswahlWertVoreinstellung(
    filterPathIntersectionCached("InspirationGroup.tsx:24")(
      inspirationen,
      (i: PInspiration) => i.Inspiration
    )
  );
  const initial = first(auswahlwertVoreinstellung["Inspiration"]) as MInspiration;
  const [currentlyFocusedInspiration, setCurrentlyFocusedInspiration] =
    useState<MInspiration>(initial);
  const sortedInsp = inspirationen.sort(
    (a, b) => a.Inspiration.Sortierung - b.Inspiration.Sortierung
  );

  function handleSelectInspiration(inspiration: MInspiration) {
    setCurrentlyFocusedInspiration(inspiration);
    setInpsirationInArrangement(inspiration);
  }

  function setInpsirationInArrangement(inspiration: MInspiration) {
    agt().zaunart = inspiration.Zaunart;
    agt().inspiration = inspiration;
    APP.logUserAction({ action: "setInspiration", Inspiration: inspiration.Inspiration });
    APP.updateUI();
  }

  function handleFormClick(e: React.MouseEvent<HTMLFormElement>) {
    const target = e.target as HTMLElement;
    const type = (target as HTMLInputElement).type;
    if (type !== "radio" && type !== "button" && target.tagName !== "A") {
      setInpsirationInArrangement(currentlyFocusedInspiration);
    }
  }

  function handleOnColorKeyDown(e: React.KeyboardEvent, inspiration: MInspiration) {
    if (e.key === "Enter") {
      setCurrentlyFocusedInspiration(inspiration);
      setInpsirationInArrangement(inspiration);
    }
    return false;
  }

  const productUrl = maxFrequent(inspirationen.map((i) => i.Inspiration.Produktlink)) as string;
  const isSelected = sortedInsp.includes(agt().inspiration);

  return (
    <form onMouseDown={handleFormClick} className={`group rounded ${isSelected && "active-group"}`} data-group-key={detailSerie.Name}>
      <div
        id={"inspiration-" + currentlyFocusedInspiration.id}
        className={`img fade`}
        style={{ backgroundImage: `url(${currentlyFocusedInspiration?.Inspiration?.Auswahlbild})` }}
      >
        <div className="system-category">
          {detailSerie?.DetailSerie?.[0]?.Serie?.[0]?.Auswahlwert?.Name + " "}
        </div>

        <button
          className="inspiration-weiter-button"
          data-button-key={"weiter-" + detailSerie.Key}
          aria-label={
            currentlyFocusedInspiration.Inspiration?.AriaText ||
            currentlyFocusedInspiration.Inspiration?.Name + " " + I18N("aria::fortfahren")
          }
          onClick={(e) => {
            e.preventDefault();
            APP.logUserAction({ action: "kachel-weiter-button" }, { stateChange: false });
            gotoPage("Zauneditor");
          }}
        >
          {I18N("Weiter ▶")}
        </button>
      </div>

      <fieldset className="colors" aria-label={I18N("aria::Farbauswahl für") + detailSerie.Name} data-select-key={"Farbauswahl-" + detailSerie.Key} data-value={currentlyFocusedInspiration?.Inspiration?.Key}>
        <legend className="sr-only">{I18N("aria::Farbvarianten")}</legend>
        {sortedInsp.map((inspiration) => (
          <label className="color-wrapper" key={inspiration.id} htmlFor={`color-${inspiration.id}`}>
            <input
              type="radio"
              name={`color-${detailSerie.id}`}
              id={`color-${inspiration.id}`}
              title={inspiration.Inspiration?.Name}
              aria-label={inspiration.Inspiration?.AriaText || inspiration.Inspiration?.Name}
              className={`color-input ${
                isSelected && currentlyFocusedInspiration === inspiration ? "selected-color" : ""
              }`}
              style={{
                order: `${-inspiration.FarbKategorie.Sortierung}`,
              }}
              onMouseDown={() => handleSelectInspiration(inspiration)}
              onKeyDown={(e) => handleOnColorKeyDown(e, inspiration)}
              defaultChecked={currentlyFocusedInspiration === inspiration}
              onChange={() => setCurrentlyFocusedInspiration(inspiration)}
            ></input>

            <div
              className={`color`}
              style={{ background: `url(${inspiration.FarbKategorie.Auswahlbild})` }}
            />
          </label>
        ))}
      </fieldset>

      <div className={classNames("title", detailSerie.Name.length > 15 && "small-text")}>
        <span>{detailSerie.Name}</span>

        {productUrl && (
          <a
            className="info icon-info-circle icon"
            title={I18N("Details zur Serie")}
            aria-label={I18N("Details zur Serie") + " " + detailSerie.Name}
            href={productUrl}
            target="_blank"
          />
        )}
      </div>
    </form>
  );
}
